/**
 * useHospitalityDetail.js
 * Logic hook for HospitalityDetailDialog — state, effects, and all handlers.
 * Stage 2 logic refactor of ActivityDialogs.
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { hospitalityAPI } from '../../../../services/api';
import api from '../../../../services/api';
import { useConfirm } from '../../../../components/common/ConfirmDialog';

export function useHospitalityDetail(hospitality, {
  associations = [],
  onHospitalityUpdate,
  canEditAccount,
  onOpenChange,
  onSuccess,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [filePreviewOpen, setFilePreviewOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [previewFileType, setPreviewFileType] = useState(null);
  const [previewFileId, setPreviewFileId] = useState(null);
  const [isEditingFile, setIsEditingFile] = useState(false);
  const [editFileDescription, setEditFileDescription] = useState('');
  const [updatingFile, setUpdatingFile] = useState(false);
  const [isRotatingFile, setIsRotatingFile] = useState(false);
  const [formData, setFormData] = useState({
    category: 'gift',
    short_summary: '',
    details: '',
    date: '',
    currency: 'USD',
    monetary_value: 0,
    recipient_association_id: '',
    approval_status: 'na',
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const confirm = useConfirm();

  // Sync hospitality → form state when hospitality changes
  useEffect(() => {
    if (hospitality) {
      setFormData({
        category: hospitality.category || 'gift',
        short_summary: hospitality.short_summary || '',
        details: hospitality.details || '',
        date: hospitality.date ? (hospitality.date).split('T')[0] : '',
        currency: hospitality.currency || 'USD',
        monetary_value: hospitality.monetary_value || 0,
        recipient_association_id: hospitality.recipient_association_id || '',
        approval_status: hospitality.approval_status || 'na',
      });
      setReceiptFile(hospitality.receipt_file_id
        ? { id: hospitality.receipt_file_id, filename: 'Receipt' }
        : null);
      setPhotoFiles(hospitality.photo_file_ids?.length
        ? hospitality.photo_file_ids.map(id => ({ id, filename: 'Photo' }))
        : []);
    }
    setIsEditing(false);
  }, [hospitality]);

  const handleSave = async () => {
    if (!hospitality?.id) return;
    setSaving(true);
    try {
      await hospitalityAPI.update(hospitality.id, {
        ...formData,
        date: formData.date ? `${formData.date}T12:00:00.000Z` : null,
        receipt_file_id: receiptFile?.id || null,
        photo_file_ids: photoFiles.map(f => f.id),
      });
      toast.success('Entry updated');
      const updated = {
        ...hospitality, ...formData,
        date: formData.date ? `${formData.date}T12:00:00.000Z` : null,
        receipt_file_id: receiptFile?.id || null,
        photo_file_ids: photoFiles.map(f => f.id),
      };
      if (onHospitalityUpdate) onHospitalityUpdate(updated);
      setIsEditing(false);
      onSuccess?.();
    } catch {
      toast.error('Failed to update entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!hospitality?.id) return;
    if (!await confirm({ title: 'Are you sure you want to delete this hospitality/gift entry?', destructive: true })) return;
    setSaving(true);
    try {
      await hospitalityAPI.delete(hospitality.id);
      toast.success('Entry deleted');
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error('Failed to delete entry');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e, type = 'photo') => {
    const files = e.target.files;
    if (!files?.length) return;
    const isReceipt = type === 'receipt';
    isReceipt ? setUploadingReceipt(true) : setUploadingPhotos(true);
    try {
      const token = localStorage.getItem('token');
      const newFiles = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('crm_entity_id', hospitality.crm_entity_id);
        fd.append('description', isReceipt ? 'Receipt' : 'Photo');
        const response = await fetch(
          `${window.location.origin}/api/files/upload`,
          { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd }
        );
        if (response.ok) newFiles.push(await response.json());
      }
      if (isReceipt && newFiles.length > 0) {
        setReceiptFile(newFiles[0]);
        toast.success('Receipt uploaded');
      } else {
        setPhotoFiles(prev => [...prev, ...newFiles]);
        toast.success(`${newFiles.length} photo(s) uploaded`);
      }
    } catch {
      toast.error('Failed to upload files');
    } finally {
      isReceipt ? setUploadingReceipt(false) : setUploadingPhotos(false);
    }
  };

  const cancelEdit = () => {
    if (hospitality) {
      setFormData({
        category: hospitality.category || 'gift',
        short_summary: hospitality.short_summary || '',
        details: hospitality.details || '',
        date: hospitality.date ? (hospitality.date).split('T')[0] : '',
        currency: hospitality.currency || 'USD',
        monetary_value: hospitality.monetary_value || 0,
        recipient_association_id: hospitality.recipient_association_id || '',
        approval_status: hospitality.approval_status || 'na',
      });
      setReceiptFile(hospitality.receipt_file_id
        ? { id: hospitality.receipt_file_id, filename: 'Receipt' }
        : null);
      setPhotoFiles(hospitality.photo_file_ids?.length
        ? hospitality.photo_file_ids.map(id => ({ id, filename: 'Photo' }))
        : []);
    }
    setIsEditing(false);
  };

  const openFileInNewTab = async (fileId, fileName = 'file') => {
    if (!fileId) { toast.error('No file available'); return; }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${window.location.origin}/api/files/${fileId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPreviewFileUrl(url);
        setPreviewFileType(blob.type);
        setPreviewFileId(fileId);
        setEditFileDescription(fileName);
        setFilePreviewOpen(true);
      } else {
        toast.error('File not found or no longer available');
      }
    } catch {
      toast.error('Failed to open file');
    }
  };

  const downloadFileFromUrl = async (url, filename) => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'file';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDeleteFile = async () => {
    if (!previewFileId) return;
    if (!await confirm({ title: 'Are you sure you want to delete this file?', destructive: true })) return;
    setUpdatingFile(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${window.location.origin}/api/files/${previewFileId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        toast.success('File deleted');
        setFilePreviewOpen(false);
        if (previewFileId === hospitality.receipt_file_id) {
          setReceiptFile(null);
        } else {
          setPhotoFiles(prev => prev.filter(f => f.id !== previewFileId));
        }
        onSuccess?.();
      } else {
        throw new Error('Failed to delete');
      }
    } catch {
      toast.error('Failed to delete file');
    } finally {
      setUpdatingFile(false);
    }
  };

  const handleUpdateFileDescription = async () => {
    if (!previewFileId || !editFileDescription.trim()) return;
    setUpdatingFile(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${window.location.origin}/api/files/${previewFileId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ description: editFileDescription }),
        }
      );
      if (response.ok) {
        toast.success('File updated');
        setIsEditingFile(false);
      } else {
        throw new Error('Failed to update');
      }
    } catch {
      toast.error('Failed to update file');
    } finally {
      setUpdatingFile(false);
    }
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      gift: 'Gift',
      hospitality_dining: 'Dining',
      hospitality_experience: 'Experience',
      hospitality_travel: 'Travel',
      hospitality_meeting: 'Meeting',
    };
    return labels[cat] || cat;
  };

  const handleRotateFile = async () => {
    if (!previewFileId || !previewFileUrl || isRotatingFile) return;
    setIsRotatingFile(true);
    try {
      const blob = await new Promise((resolve, reject) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalHeight;
          canvas.height = img.naturalWidth;
          const ctx = canvas.getContext('2d');
          ctx.translate(img.naturalHeight / 2, img.naturalWidth / 2);
          ctx.rotate(Math.PI / 2);
          ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
          canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/jpeg', 0.92);
        };
        img.onerror = reject;
        img.src = previewFileUrl;
      });
      const fd = new FormData();
      fd.append('file', new File([blob], 'rotated.jpg', { type: 'image/jpeg' }));
      const uploadRes = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const newFile = uploadRes.data;
      await api.delete(`/files/${previewFileId}`).catch(() => {});
      const newUrl = URL.createObjectURL(blob);
      setPreviewFileUrl(newUrl);
      setPreviewFileId(newFile.id);
      setPhotoFiles(prev => prev.map(f => f.id === previewFileId ? { ...f, id: newFile.id } : f));
      toast.success('Image rotated');
    } catch {
      toast.error('Failed to rotate image');
    } finally {
      setIsRotatingFile(false);
    }
  };

  // Derived values
  const canEdit = canEditAccount !== undefined ? canEditAccount : true;
  const recipientAssoc = associations.find(a => a.id === hospitality?.recipient_association_id);

  return {
    isEditing, setIsEditing,
    saving,
    uploadingReceipt, uploadingPhotos,
    filePreviewOpen, setFilePreviewOpen,
    previewFileUrl, previewFileType, previewFileId,
    isEditingFile, setIsEditingFile,
    editFileDescription, setEditFileDescription,
    updatingFile,
    formData, setFormData,
    receiptFile, photoFiles, setReceiptFile, setPhotoFiles,
    handleSave, handleDelete, handleFileUpload, cancelEdit,
    openFileInNewTab, downloadFileFromUrl,
    handleDeleteFile, handleUpdateFileDescription,
    handleRotateFile, isRotatingFile,
    getCategoryLabel,
    canEdit, recipientAssoc,
  };
}
